import api from "@/api";
import i18n from "@/i18n";
import { createLatestRequestGate, parseApiError, parseCollectionAuthorization } from "@/helpers/teamAuthorization";

const { t } = i18n.global;
const rolesRequestGate = createLatestRequestGate();
export default {
  state: {
    roles: [],
    role: {},
    selectedRole: {},
    collectionAuthorization: null,
    collectionError: null,
  },

  getters: {
    roles: (state) => state.roles,
    role: (state) => state.role,

    selectedRole: (state) => state.selectedRole,
    roleCollectionAuthorization: (state) => state.collectionAuthorization,
    roleCollectionError: (state) => state.collectionError,
  },

  mutations: {
    setRoles: (state, data) => {
      state.roles = data;
    },
    setRole: (state, data) => {
      state.role = data;
    },

    setSelectedRole(state, role) {
      state.selectedRole = role;  
    },
    setRoleCollectionAuthorization(state, data) {
      state.collectionAuthorization = data;
    },
    setRoleCollectionError(state, data) {
      state.collectionError = data;
    },
  },

  actions: {
    getRoles: async ({ commit }) => {
      try {
        const response = await api({ requiresAuth: true }).get("/v1/roles");
        const { data: roles } = response.data;
        commit("setRoles", roles);
      } catch (err) { 
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
      }
    },

    getRolesByTeam: async ({ commit }, teamId) => {
      const normalizedTeamId = Number(teamId);
      commit("setRoles", []);
      commit("setSelectedRole", {});
      commit("setRoleCollectionAuthorization", null);
      commit("setRoleCollectionError", null);
      if (!Number.isInteger(normalizedTeamId) || normalizedTeamId <= 0) return;

      const requestKey = `roles:${normalizedTeamId}`;
      const { signal } = rolesRequestGate.start(requestKey);
      try {
        const response = await api({ requiresAuth: true }).get(
          `/v1/teams/${normalizedTeamId}/roles`,
          { signal }
        );
        if (!rolesRequestGate.isActive(requestKey)) return;
        const { data: roles } = response.data;
        commit("setRoles", roles);
        commit("setRoleCollectionAuthorization", parseCollectionAuthorization(response));
      } catch (err) {
        if (err.code === "ERR_CANCELED" || !rolesRequestGate.isActive(requestKey)) return;
        commit("setRoleCollectionError", parseApiError(err));
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
      }
    },

    getRole: async ({ commit }, id) => {
      try {
        const response = await api({ requiresAuth: true }).get(
          `/v1/roles/${id}`
        );
        const { data: role } = response.data;
        commit("setRole", role);
      } catch (err) { 
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
      }
    },

    updateRole: async ({ commit }, { id, title, permissions, teamId }) => {
      try {
        return await api({ requiresAuth: true }).patch(`/v1/roles/${id}`, {
          title,
          permissions,
          team_id: teamId,
        });
      } catch (err) { 
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
        throw err;
      }
    },

    createRole: async ({ commit }, { title, permissions, team_id }) => {
      try {
        const response = await api({ requiresAuth: true }).post("/v1/roles", {
          title,
          permissions,
          team_id
        });
        commit("setToast", {
          display: true,
          type: "success",
          message: t("messages.roleCreatedSuccessfully"),
        });
        return response;
      } catch (err) { 
        commit("setToast", {
          display: true,
          type: "error",
          message: t("messages.somethingWentWrong"),
        });
        throw err;
      }
    },

    deleteRole: async ({ commit }, { id, teamId }) => {
      try {
        return await api({ requiresAuth: true }).delete(`/v1/roles/${id}`, {
          data: { team_id: teamId },
        });
      } catch (err) {
        commit("setToast", {
          display: true,
          type: "error",
          message: err.response?.data?.message || t("messages.somethingWentWrong"),
        });
        throw err;
      }
    },
  },
};
